import React, { useState } from 'react';
import Select from '../../components/common/Select';
import { connect } from 'react-redux';
import {
  GET_USER_ROLES,
  SET_USER_ROLES,
  CLEAR_USER_ROLES
} from '../../service/types';
import { getUserRoles } from '../../service/actions/users';
import { setUserRoles } from '../../service/actions/userRoles';
import { createLoadingSelector } from '../../service/selectors';

import loadingSpinner from '../../assets/images/spinner.svg';

const UserRolesModal = ({
  user,
  hideModal,
  rolesData,
  loading,
  rolesError,
  fetchUserRoles,
  clearUserRoles,
  setUserRoles
}) => {
  // Only fetch user roles when user changes and modal is open, not when rolesData changes
  React.useEffect(() => {
    // If no user, don't fetch
    if (!user || !user.username) return;
    // If rolesData is present, don't fetch
    if (rolesData) return;
    // If modal is closing, don't fetch (rolesData will be cleared)
    let isActive = true;
    // Fetch roles for the current user
    fetchUserRoles(user.username);
    // Cleanup: mark inactive so fetch isn't triggered after modal closes
    return () => {
      isActive = false;
    };
  }, [user, fetchUserRoles]);

  const [selectedRole, setSelectedRole] = useState('');

  const assignedRoles = rolesData && rolesData.groups ? rolesData.groups : [];
  const unassignedRoles =
    rolesData && rolesData.availableRoles
      ? rolesData.availableRoles.filter(role => !assignedRoles.includes(role))
      : [];

  const handleRoleChange = ({ value }) => {
    setSelectedRole(value);
  };

  const handleAddRole = async () => {
    if (selectedRole && selectedRole !== 'Select a role') {
      const updatedRoles = [...assignedRoles, selectedRole];
      try {
        await setUserRoles(user.username, updatedRoles);
        setSelectedRole('');
        await fetchUserRoles(user.username);
      } catch (err) {
        console.error('Failed to update roles:', err);
      }
    }
  };

  const handleRemoveRole = async role => {
    const updatedRoles = assignedRoles.filter(r => r !== role);
    try {
      await setUserRoles(user.username, updatedRoles);
      await fetchUserRoles(user.username);
    } catch (err) {
      console.error('Failed to update roles:', err);
    }
  };

  // Clear rolesData when modal closes
  const handleClose = () => {
    clearUserRoles();
    hideModal();
  };

  const isLoading = loading || !rolesData;

  return (
    <div style={{ position: 'relative' }}>
      {/* Overlay Spinner */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(255,255,255,0.7)',
            zIndex: 10,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pointerEvents: 'all'
          }}
        >
          <img
            alt="Loading..."
            src={loadingSpinner}
            style={{ width: 60, height: 60 }}
          />
        </div>
      )}
      {/* Modal Content */}
      <div style={isLoading ? { pointerEvents: 'none', opacity: 0.5 } : {}}>
        <h2>{`${user.firstName} ${user.lastName}`}</h2>
        <p>
          <strong>Username:</strong> {user.username}
        </p>
        <p>
          <strong>Email:</strong> {user.email}
        </p>
        {rolesError && (
          <div style={{ color: 'red', textAlign: 'center', margin: '1em 0' }}>
            {rolesError}
          </div>
        )}
        {rolesData && (
          <div>
            <div style={{ marginBottom: '1em' }}>
              <strong>Assigned Roles:</strong>
              {assignedRoles.length > 0 ? (
                <div
                  style={{
                    marginTop: '0.5em',
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: '0.5em'
                  }}
                >
                  {assignedRoles.map(role => (
                    <span
                      key={role}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        background: '#f2f2f2',
                        borderRadius: '16px',
                        padding: '0.25em 0.75em',
                        fontSize: '0.95em'
                      }}
                    >
                      {role}
                      <button
                        onClick={() => handleRemoveRole(role)}
                        style={{
                          marginLeft: '0.5em',
                          background: 'none',
                          border: 'none',
                          color: '#c00',
                          cursor: loading ? 'not-allowed' : 'pointer',
                          fontWeight: 'bold',
                          fontSize: '1em'
                        }}
                        title={`Remove ${role}`}
                        disabled={loading}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              ) : (
                <span style={{ marginLeft: '0.5em' }}>None</span>
              )}
            </div>
            <hr />
            <div style={{ margin: '1em 0' }}>
              <label htmlFor="add-role-select">
                <strong>Add Role:</strong>
              </label>{' '}
              <div style={{ display: 'inline-block', minWidth: 180 }}>
                <Select
                  formValue={{
                    name: 'add-role-select',
                    labelTitle: '',
                    valueId: 'add-role'
                  }}
                  optionList={['Select a role', ...unassignedRoles]}
                  startingValue={selectedRole || 'Select a role'}
                  controlFunc={handleRoleChange}
                  emptyValueOption={'Select a role'}
                  showClearButton={false}
                  disabled={unassignedRoles.length === 0 || isLoading}
                />
              </div>
              <button
                onClick={handleAddRole}
                disabled={
                  isLoading || !selectedRole || selectedRole === 'Select a role'
                }
                style={{ marginLeft: '0.5em' }}
              >
                Add
              </button>
            </div>
          </div>
        )}
        <button onClick={handleClose} style={{ marginTop: '1em' }}>
          Close
        </button>
      </div>
    </div>
  );
};

const loadingSelector = createLoadingSelector([GET_USER_ROLES, SET_USER_ROLES]);
const mapStateToProps = state => ({
  rolesData: state.users.rolesData,
  loading: loadingSelector(state),
  rolesError: state.users.rolesError
});

const mapDispatchToProps = dispatch => ({
  fetchUserRoles: username => dispatch(getUserRoles(username)),
  clearUserRoles: () => dispatch({ type: CLEAR_USER_ROLES }),
  setUserRoles: (username, roles) => dispatch(setUserRoles(username, roles))
});

export default connect(mapStateToProps, mapDispatchToProps)(UserRolesModal);
